import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Database, ArrowRight, Check, AlertCircle,
  Layers, Workflow, BarChart3, DollarSign,
  Clock, FileSearch, Gauge
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function DataEngineeringPage() {
  const problems = [
    'Data spread across multiple systems and spreadsheets.',
    'Slow, manual reporting cycles.',
    'Lack of a single source of truth.',
    'Dashboards that don\'t reflect real-time operations.'
  ];

  const outcomes = [
    { icon: Layers, title: 'Modern data stack', description: 'Centralized, governed and documented data.' },
    { icon: Workflow, title: 'Automated pipelines', description: 'Ingest, transform and load data on reliable schedules.' },
    { icon: BarChart3, title: 'Actionable analytics', description: 'Dashboards and reports aligned to business decisions.' },
    { icon: DollarSign, title: 'Cost visibility', description: 'Understand cost, usage and performance across your data platform.' }
  ];

  const scope = [
    'Data discovery and modeling',
    'Data warehouse / data lake design and implementation',
    'ETL/ELT pipelines (batch and near real-time)',
    'BI dashboards and self-service analytics',
    'Data quality, observability and governance basics'
  ];

  const engagements = [
    { icon: FileSearch, title: 'Quick assessment', duration: '4–6 weeks', description: 'Readiness assessment and roadmap.' },
    { icon: Database, title: 'Data platform build', duration: '3–6 months', description: 'Architecture, build, rollout.' },
    { icon: Gauge, title: 'Ongoing support', duration: 'Monthly retainer', description: 'Enhancements and maintenance.' }
  ];

  const useCases = [
    {
      title: 'Unified Data Platform for a SaaS Company',
      problem: 'Customer data fragmented across 5+ tools, making churn predictions impossible.',
      solution: 'Built a centralized data warehouse with automated pipelines from all sources.',
      result: 'Reduced reporting time from 2 weeks to 2 hours. Improved retention by 15%.'
    },
    {
      title: 'Real-time Analytics for Operations',
      problem: 'Logistics company relied on end-of-day reports, missing optimization opportunities.',
      solution: 'Implemented near real-time data pipelines with live dashboards.',
      result: 'Real-time decisions enabled. Route optimization improved fuel efficiency by 12%.'
    }
  ];

  return (
    <div data-testid="data-engineering-page">
      <Helmet>
        <title>Data Engineering & Analytics - Cynergy Services</title>
        <meta name="description" content="Modern data platforms, reliable pipelines and dashboards that your teams actually use. Data engineering services by Cynergy." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="data-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-medium uppercase tracking-widest text-primary">Service</p>
              </div>
              <h1 className="heading-1 text-foreground mb-4">Data Engineering & Analytics</h1>
              <p className="body-lg mb-6">Modern data platforms, reliable pipelines and dashboards that your teams actually use.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact">
                  <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="data-cta-primary">
                    Request a data platform review
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="data-cta-secondary">
                    Book a 30-minute call
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/fwhoo3fb_Data%20Engineering%20.jpeg"
                alt="Data analytics dashboard"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems & Outcomes Combined */}
      <section className="section-padding bg-white" data-testid="data-problems-outcomes">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Problems */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Challenges</p>
              <h2 className="heading-2 text-foreground mb-6">Problems we solve</h2>
              <ul className="space-y-3">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`data-problem-${index}`}>
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Outcomes */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Results</p>
              <h2 className="heading-2 text-foreground mb-6">Outcomes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {outcomes.map((outcome, index) => (
                  <Card key={outcome.title} className="bg-secondary/30 border-0" data-testid={`data-outcome-${index}`}>
                    <CardContent className="p-4">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center mb-3">
                        <outcome.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {outcome.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">{outcome.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scope & Engagements */}
      <section className="section-padding bg-secondary/50" data-testid="data-scope">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Capabilities</p>
              <h2 className="heading-2 text-foreground mb-6">What we typically deliver</h2>
              <ul className="space-y-3">
                {scope.map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`data-scope-${index}`}>
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Engagement Models</p>
              <h2 className="heading-2 text-foreground mb-6">How we work</h2>
              <div className="space-y-3">
                {engagements.map((engagement, index) => (
                  <Card key={engagement.title} className="border-border/60" data-testid={`data-engagement-${index}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <engagement.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {engagement.title}
                          </h3>
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                            {engagement.duration}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{engagement.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-white" data-testid="data-use-cases">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Case Studies</p>
            <h2 className="heading-2 text-foreground">Example use cases</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-secondary/30 border-0" data-testid={`data-use-case-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="heading-3 text-foreground mb-4">{useCase.title}</h3>
                    <div className="space-y-3 text-sm">
                      <p><span className="font-medium text-primary">Problem:</span> <span className="text-muted-foreground">{useCase.problem}</span></p>
                      <p><span className="font-medium text-primary">Solution:</span> <span className="text-muted-foreground">{useCase.solution}</span></p>
                      <p><span className="font-medium text-primary">Result:</span> <span className="text-muted-foreground">{useCase.result}</span></p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary" data-testid="data-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">Want to modernize your data stack?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Let's discuss how we can help you build a modern data platform.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" data-testid="data-final-cta">
                Start with a data consultation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
